import { useEffect } from 'react';
import { QueryProvider } from '@/shared/providers';
import { AppRouter } from '@/app/routes';
import { useTheme } from '@/shared/hooks/useTheme';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { getCurrentUser } from '@/features/auth/api';
import '@/shared/i18n';
import '@/styles/globals.css';
//import 'firebase/auth'; //TODO: add firebase auth

function decodeUuidFromAccessToken(token?: string): string {
  if (!token) return '';
  try {
    const [, payload] = token.split('.');
    if (!payload) return '';
    const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const parsed = JSON.parse(atob(padded)) as { uuid?: string };
    return String(parsed.uuid || '');
  } catch {
    return '';
  }
}

/**
 * Root App component
 * Sets up providers and router
 */
function App() {
  // Initialize theme from localStorage on mount
  useTheme();

  // Refresh user profile from backend on app load to keep data fresh.
  // /users/user does NOT always return JWT-derived fields (e.g. role/uuid),
  // so preserve them from the stored auth session.
  const { isAuthenticated, setUser } = useAuthStore();
  useEffect(() => {
    if (isAuthenticated) {
      getCurrentUser()
        .then((freshUser) => {
          const stored = useAuthStore.getState().user;
          const accessToken = useAuthStore.getState().tokens?.accessToken;
          const tokenUuid = decodeUuidFromAccessToken(accessToken);
          setUser({
            ...freshUser,
            uuid: tokenUuid || freshUser.uuid || stored?.uuid || '',
            role: stored?.role ?? 'SENDER',
            accountType: stored?.accountType,
          });
        })
        .catch(() => {/* silent — stale cached data is fine */});
    }
  }, [isAuthenticated, setUser]);

  return (
    <QueryProvider>
      <AppRouter />
    </QueryProvider>
  );
}

export default App;
